import type { EstimateOption } from "../api/pricing";

/**
 * Catalog funding methods (UPPER_SNAKE). Mirrors the canonical list in
 * `raas-widgets-backend/src/catalog/constants/catalog.constants.ts`. These are
 * the ways the SENDER can pay for the transfer.
 */
export const FUNDING_METHODS = [
  "DEBIT_CARD",
  "CREDIT_CARD",
  "ACH",
  "RTP",
  "WALLET",
  "CASH_DEPOSIT",
  "BANK_BALANCE",
] as const;
export type FundingMethod = (typeof FUNDING_METHODS)[number];

export const PAYOUT_METHODS = [
  "CASH_PICKUP",
  "BANK_ACCOUNT",
  "PUSH_TO_CARD",
  "MOBILE_WALLET",
  "HOME_DELIVERY",
] as const;
export type PayoutMethod = (typeof PAYOUT_METHODS)[number];

/**
 * UI category used by the funding selector: CARD (needs a stored instrument),
 * ACCOUNT (needs a stored instrument), CASH (paid in person, no instrument).
 */
export type FundingCategory = "CARD" | "ACCOUNT" | "CASH" | "OTHER";

export const FUNDING_METHOD_CATEGORY: Record<FundingMethod, FundingCategory> = {
  DEBIT_CARD: "CARD",
  CREDIT_CARD: "CARD",
  ACH: "ACCOUNT",
  RTP: "ACCOUNT",
  BANK_BALANCE: "ACCOUNT",
  WALLET: "OTHER",
  CASH_DEPOSIT: "CASH",
};

/**
 * Classify any funding method string into a UI category. Accepts both the
 * canonical catalog vocabulary and the legacy lowercase trio.
 */
export function categoryFromFundingMethod(
  method: string | undefined | null
): FundingCategory | undefined {
  if (!method) return undefined;
  const upper = method.toUpperCase();
  if (upper in FUNDING_METHOD_CATEGORY) {
    return FUNDING_METHOD_CATEGORY[upper as FundingMethod];
  }
  switch (method) {
    case "card":
      return "CARD";
    case "bank_account":
      return "ACCOUNT";
    case "cash":
      return "CASH";
    default:
      return undefined;
  }
}

/**
 * Distinct funding methods available across a set of pre-quote options (or
 * raw funding-method strings from facets), in catalog order.
 */
export function availableFundingMethods(
  optionsOrMethods: Array<Pick<EstimateOption, "fundingMethod"> | string>
): FundingMethod[] {
  const seen = new Set(
    optionsOrMethods.map((o) =>
      typeof o === "string" ? o : o.fundingMethod
    )
  );
  return FUNDING_METHODS.filter((m) => seen.has(m));
}

/**
 * Cheapest (lowest sendAmount) option whose funding method matches the given
 * canonical method, from an already-filtered option list.
 */
export function optionForFundingMethod(
  options: EstimateOption[],
  method: FundingMethod
): EstimateOption | undefined {
  return options
    .filter((o) => o.fundingMethod === method)
    .sort((a, b) => a.sendAmount - b.sendAmount)[0];
}

/**
 * Best customerFee for a funding method: facets (pre-collapse) first, then
 * the cheapest matching option. `0` from the engine is kept; missing → null.
 */
export function bestCustomerFeeForFundingMethod(
  method: FundingMethod,
  facets?: {
    fundingMethods?: Array<{
      fundingMethod: string;
      best?: { customerFee?: number | null };
    }>;
  } | null,
  options?: Array<{
    fundingMethod?: string;
    customerFee?: number | null;
    sendAmount: number;
  }> | null
): number | null {
  const target = method.toUpperCase();
  const fromFacet = facets?.fundingMethods?.find(
    (row) => (row.fundingMethod ?? "").toUpperCase() === target
  )?.best?.customerFee;
  if (fromFacet != null) return fromFacet;
  const fromOption = (options ?? [])
    .filter((o) => (o.fundingMethod ?? "").toUpperCase() === target)
    .sort((a, b) => a.sendAmount - b.sendAmount)[0]?.customerFee;
  return fromOption != null ? fromOption : null;
}

/**
 * Send B: prefer the scoped (payout+payer) best fee when it exists; otherwise
 * discovery. Keeps `0` from either source; `null` only when both miss.
 */
export function methodEstimateFee(args: {
  method: FundingMethod;
  scopedFacets?: Parameters<typeof bestCustomerFeeForFundingMethod>[1];
  scopedOptions?: Parameters<typeof bestCustomerFeeForFundingMethod>[2];
  discoveryFacets?: Parameters<typeof bestCustomerFeeForFundingMethod>[1];
  discoveryOptions?: Parameters<typeof bestCustomerFeeForFundingMethod>[2];
}): number | null {
  const scoped = bestCustomerFeeForFundingMethod(
    args.method,
    args.scopedFacets,
    args.scopedOptions
  );
  if (scoped != null) return scoped;
  return bestCustomerFeeForFundingMethod(
    args.method,
    args.discoveryFacets,
    args.discoveryOptions
  );
}

/**
 * Resolves the live pre-quote SKU for a composed send selection.
 *
 * When the payout pins a catalog payer (cash pickup / wallet), that payer must
 * win — never the cheapest CASH_PICKUP/MOBILE_WALLET rail for the funding
 * method alone (that overwrote Elektra with Air Pak / Caja Popular).
 */
export function resolveSelectedEstimateOption(
  options: EstimateOption[],
  args: {
    payoutMethod: string;
    fundingMethod: FundingMethod;
    /** Catalog payer / carrier code when the payout pins one. */
    payerCode?: string | null;
    /** Option captured at payout-details submit, if still live. */
    preferred?: EstimateOption | null;
  }
): EstimateOption | null {
  const funded = options.filter(
    (o) =>
      o.payoutMethod === args.payoutMethod &&
      o.fundingMethod === args.fundingMethod
  );
  if (funded.length === 0) return null;

  const payer = args.payerCode?.trim();
  if (payer) {
    const byPayer = funded.filter((o) => o.payerCode === payer);
    if (byPayer.length === 0) return null;
    if (
      args.preferred &&
      byPayer.some((o) => o.capabilityKey === args.preferred!.capabilityKey)
    ) {
      return args.preferred;
    }
    return [...byPayer].sort((a, b) => a.sendAmount - b.sendAmount)[0] ?? null;
  }

  if (
    args.preferred &&
    funded.some((o) => o.capabilityKey === args.preferred!.capabilityKey)
  ) {
    return args.preferred;
  }
  return [...funded].sort((a, b) => a.sendAmount - b.sendAmount)[0] ?? null;
}
